import React, { useState, useEffect } from 'react';
import { CheckCircle2, Lock, Smartphone, CreditCard } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { usePendingBills } from '../../../../hooks';
import { useTranslation } from 'react-i18next';

interface Props {
  onNext?: () => void;
  onPrevious?: () => void;
  allStepData?: any;
  onDataChange?: (data: any) => void;
  data?: any;
  onGoToStep?: (step: number) => void;
}

export const SelectBillsAndPay: React.FC<Props> = ({ onNext, onPrevious, allStepData, data, onDataChange, onGoToStep }) => {
  const { t } = useTranslation();
  const accountData = allStepData?.[0]?.accountData;
  const [selectedMethod, setSelectedMethod] = useState<string>('Digital Wallet');
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  // Mock bills (in reality fetched via API)
  const [bills, setBills] = useState<any[]>([]);

  const { data: billsData, isFetching } = usePendingBills({
    remote_utility_id: accountData?.remoteUtilityId?.toString(),
    consumer_number: accountData?.consumerNo || '',
  }, {
    enabled: !!accountData?.consumerNo
  });

  useEffect(() => {
    const pendingBills = billsData?.pendingBills || billsData?.result?.pendingBills;
    if (pendingBills) {
      setBills(
        pendingBills.map((b: any) => ({
          id: b.remoteBillId || b.invoiceNo,
          month: new Date(b.billDate).toLocaleString('default', { month: 'long', year: 'numeric' }),
          status: b.billPaymentStatus === 'UNPAID' ? 'OVERDUE' : b.billPaymentStatus,
          amount: b.outstandingAmount || b.billAmount || 0,
          dueDate: b.dueDate,
          usage: '', 
          lateFee: 0, 
          selected: true
        }))
      );
    }
  }, [billsData]);

  const toggleBill = (id: number) => {
    setBills(bills.map(b => b.id === id ? { ...b, selected: !b.selected } : b));
  };

  const selectedBills = bills.filter(b => b.selected);
  const subtotal = selectedBills.reduce((acc, curr) => acc + curr.amount, 0);
  const totalLateFees = selectedBills.reduce((acc, curr) => acc + (curr.lateFee || 0), 0);
  const taxes = 0; // Removed mock fixed tax
  const finalTotal = subtotal + totalLateFees + taxes;

  const handleSubmit = () => {
    if (finalTotal <= 0) return;
    onDataChange?.({
      ...data,
      paymentDetails: {
        method: selectedMethod,
        amount: finalTotal,
        billsCount: selectedBills.length,
        transactionId: `TXN-${Math.floor(10000000 + Math.random() * 90000000)}`,
        date: new Date().toLocaleString()
      }
    });
    onNext?.();
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column */}
      <div className="flex-1 space-y-6">
        
        {/* Success Header */}
        <div className="bg-white rounded-2xl p-5 border border-green-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" />
            <div>
              <h3 className="font-bold text-gray-900 text-sm">{`${accountData?.firstName || ''} ${accountData?.lastName || ''}`.trim()}</h3>
              <p className="text-xs text-gray-500 font-medium">{accountData?.consumerNo} • {accountData?.ownershipDisplay}</p>
            </div>
          </div>
          <button 
            className="text-sm font-semibold text-green-600 hover:text-green-700 hover:underline"
            onClick={() => onGoToStep?.(0)}
          >
            {t("Change account")}
          </button>
        </div>

        {/* Unpaid Bills */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">{t("Unpaid Bills")}</h2>
            <button 
              className="text-sm font-semibold text-blue-600 hover:underline"
              onClick={() => setBills(bills.map(b => ({ ...b, selected: false })))}
            >
              {t("Deselect All")}
            </button>
          </div>

          <div className="space-y-3">
            {isFetching ? (
              <div className="p-8 text-center text-gray-500">
                {t("Loading bills...")}
              </div>
            ) : bills.length === 0 ? (
              <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl border border-gray-200">
                {t("No pending bills found.")}
              </div>
            ) : (
              bills.map(bill => (
                <div 
                  key={bill.id} 
                  className={`flex items-center justify-between p-4 rounded-xl border ${bill.selected ? 'border-blue-600 bg-blue-50/30' : 'border-gray-200 bg-white'} cursor-pointer transition-colors`}
                  onClick={() => toggleBill(bill.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${bill.selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
                      {bill.selected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-900 text-sm">{bill.month}</span>
                        {bill.status === 'OVERDUE' && (
                          <span className="text-[10px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded uppercase tracking-wider">{t("OVERDUE")}</span>
                        )}
                        {bill.lateFee > 0 && (
                          <span className="text-[10px] font-bold bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded uppercase tracking-wider">{t("Late fee: ${{amount}}", { amount: bill.lateFee.toFixed(2) })}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{t("Due {{date}}", { date: bill.dueDate })}{bill.usage ? ` • ${bill.usage}` : ''}</p>
                    </div>
                  </div>
                  <div className="font-bold text-blue-600 text-lg">
                    ${bill.amount.toFixed(2)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Payment Method */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4">{t("Payment Method")}</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div 
              className={`p-4 rounded-xl border cursor-pointer flex items-center gap-3 transition-colors ${selectedMethod === 'Credit Card' ? 'border-blue-600 bg-blue-50/50 ring-1 ring-blue-600' : 'border-gray-200 bg-white hover:border-gray-300'}`}
              onClick={() => setSelectedMethod('Credit Card')}
            >
              <CreditCard className={`w-5 h-5 ${selectedMethod === 'Credit Card' ? 'text-blue-600' : 'text-gray-400'}`} />
              <div>
                <h4 className="font-bold text-sm text-gray-900">{t("Online Payment")}</h4>
                <p className="text-[10px] text-gray-500 font-medium">{t("UPI / Net Banking / Debit/Credit Card")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="pt-6 border-t border-gray-200 space-y-6">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-500">
              <span>{t("Subtotal")}</span>
              <span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
            </div>
              <div className="flex justify-between text-gray-500">
                <span>{t("Late Fees")}</span>
                <span className="font-semibold text-red-500">${totalLateFees.toFixed(2)}</span>
              </div>
            <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-100">
              <span>{t("Total")}</span>
              <span className="text-blue-600">${finalTotal.toFixed(2)}</span>
            </div>
          </div>

          <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer">
            <input 
              type="checkbox" 
              checked={isAuthorized}
              onChange={(e) => setIsAuthorized(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300" 
            />
            <span className="text-xs text-gray-600 leading-relaxed">
              {t("I authorize this payment of")} <strong className="text-gray-900">${finalTotal.toFixed(2)}</strong> {t("and agree to the Terms of Service. This payment is non-refundable once processed.")}
            </span>
          </label>

          <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
            <Lock className="w-3.5 h-3.5" /> {t("256-bit TLS · PCI DSS compliant · Secured by Stripe")}
          </div>

          <Button 
            onClick={handleSubmit}
            disabled={finalTotal <= 0 || !isAuthorized}
            className="w-full"
          >
            {t("Pay ${{amount}}", { amount: finalTotal.toFixed(2) })}
          </Button>
        </div>
      </div>

      {/* Right Column */}
      <div className="w-full lg:w-[380px] space-y-4">
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm  top-6">
          <h3 className="font-bold text-gray-900 mb-6">{t("Payment Summary")}</h3>

          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <p className="font-bold text-gray-900 text-sm">{`${accountData?.firstName || ''} ${accountData?.lastName || ''}`.trim()}</p>
            <p className="text-xs text-gray-500 font-medium">{accountData?.consumerNo} • {accountData?.ownershipDisplay}</p>
          </div>

          <p className="text-xs font-bold text-gray-400  tracking-wider mb-3">{t("{{count}} Bills Selected", { count: selectedBills.length })}</p>
          
          <div className="space-y-3 mb-6">
            {selectedBills.map(bill => (
              <div key={bill.id} className="flex justify-between text-sm">
                <span className="text-gray-600">{bill.month}</span>
                <span className="font-semibold text-gray-900">${bill.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>

          <div className="space-y-3 py-4 border-t border-gray-100">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t("Subtotal")}</span>
              <span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
            </div>
            {totalLateFees > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t("Late Fees")}</span>
                <span className="font-semibold text-red-500">${totalLateFees.toFixed(2)}</span>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center py-4 border-t border-gray-100 mb-4">
            <span className="font-bold text-gray-900 text-lg">{t("Total")}</span>
            <span className="font-bold text-blue-600 text-2xl">${finalTotal.toFixed(2)}</span>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500 font-medium bg-gray-50 py-2 px-3 rounded-lg border border-gray-100">
            {selectedMethod === 'Digital Wallet' ? <Smartphone className="w-3.5 h-3.5" /> : <CreditCard className="w-3.5 h-3.5" />}
            {selectedMethod}
          </div>
        </div>

        <div className="bg-[#f4f7f9] rounded-2xl p-5 border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-green-600" />
            <h4 className="font-bold text-gray-700 text-sm">Payment Security</h4>
          </div>
          <ul className="text-xs text-gray-500 space-y-2 font-medium">
            <li>• 256-bit TLS encryption</li>
            <li>• PCI DSS Level 1 compliant</li>
            <li>• Powered by Stripe</li>
            <li>• No card data stored on our servers</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
